import request from '@oskecare/fe-utilities/lib/request';
import { SUBSCRIPTION } from '@oskecare/fe-utilities/lib/constants/dataSources.js';
import CnData from './CnData.js';
import SnData from './SnData.js';
import { cacheWithErrorCheck } from '../../../helpers.js';

export default class CustomerData {
  /**
   * @type {Map<string, Promise<any>>}
   */
  _cache = new Map();

  /**
   * @param {?{
   *   cn: string,
   *   isCustomerAdmin: boolean,
   *   isPrepaid: boolean,
   *   sn: string,
   *   subAccount: string,
   *   subId: string
   * }} userData
   * @param {?string} authApiUrl
   * @param {string} subscriptionApiUrl
   * @param {string} logSource
   * @param {boolean} isFatal - set true if failure to fetch fully disables application
   */
  constructor({
    userData = null,
    authApiUrl = null,
    subscriptionApiUrl,
    logSource,
    isFatal = false,
  }) {
    if (!userData && !authApiUrl) {
      throw new Error(
        'Cannot construct CustomerData. One of "userData" or "authApiUrl" is mandatory.',
      );
    }
    if (!subscriptionApiUrl) {
      throw new Error('Cannot construct CustomerData. "subscriptionInfoApiUrl" is mandatory.');
    }
    if (!logSource) {
      throw new Error('Cannot construct CustomerData. "logSource" is mandatory.');
    }

    if (userData) {
      this._cache.set(
        'userData',
        new Promise(resolve => {
          resolve(userData);
        }),
      );
    }
    this._authApiUrl = authApiUrl;
    this._subscriptionApiUrl = subscriptionApiUrl;
    this._logSource = logSource;
    this._isFatal = isFatal;
  }

  /**
   * @return {Promise<void>}
   */
  async load() {
    this._cnData = await this._getCnData();
    this._snDataList = await this._getSnDataList();
  }

  /**
   * @return {CnData}
   */
  get cnData() {
    if (!this._cnData) {
      throw new Error(
        'CustomerData.cnData getter is only available after calling and awaiting CustomerData.load()',
      );
    }
    return this._cnData;
  }

  /**
   * @return {SnData[]}
   */
  get snDataList() {
    if (!this._snDataList) {
      throw new Error(
        'CustomerData.snDataList getter is only available after calling and awaiting CustomerData.load()',
      );
    }

    if (!this.cnData.isCustomerAdmin) {
      return this._snDataList.filter(item => item.isLoggedIn);
    }
    return this._snDataList.sort((a, b) => b.isLoggedIn - a.isLoggedIn);
  }

  /**
   * @param {string} sn
   * @return {boolean}
   */
  hasSnDataBySn(sn) {
    return this.snDataList.some(item => item.sn === sn);
  }

  /**
   * @param {string} sn
   * @return {SnData}
   */
  getSnDataBySn(sn) {
    const snData = this.snDataList.find(item => item.sn === sn);
    if (!snData) {
      throw new Error(`No snData found for sn "${sn}".`);
    }
    return snData;
  }

  /**
   * @param {string} subscriptionId
   * @return {boolean}
   */
  hasSnDataBySubscriptionId(subscriptionId) {
    return this.snDataList.some(item => item.subscriptionId === subscriptionId);
  }

  /**
   * @param {string} subscriptionId
   * @return {SnData}
   */
  getSnDataBySubscriptionId(subscriptionId) {
    const snData = this.snDataList.find(item => item.subscriptionId === subscriptionId);
    if (!snData) {
      throw new Error(`No snData found for subscriptionId "${subscriptionId}".`);
    }
    return snData;
  }

  /**
   * @return {SnData}
   */
  get loggedInSnData() {
    const snData = this.snDataList.find(item => item.isLoggedIn);
    if (!snData) {
      throw new Error(`No snData found for logged-in SN.`);
    }
    return snData;
  }

  /**
   * @return {Promise<CnData>}
   */
  async _getCnData() {
    return new CnData({
      userData: await this._getUserData(),
      loggedInSubscriptionInfo: await this._getLoggedInSubscriptionInfo(),
    });
  }

  /**
   * @return {Promise<SnData[]>}
   */
  async _getSnDataList() {
    const userData = await this._getUserData();
    return (await this._getSubscriptionInfoSearch()).map(
      subscriptionInfoSearch =>
        new SnData({
          subscriptionInfoSearch,
          userData,
        }),
    );
  }

  /**
   * @return {Promise<{
   *   cn: string,
   *   isCustomerAdmin: boolean,
   *   isPrepaid: boolean,
   *   sn: string,
   *   subAccount: string,
   *   subId: string
   * }>}
   * @private
   */
  _getUserData() {
    return cacheWithErrorCheck(this._cache, 'userData', () =>
      request({
        method: 'GET',
        route: `${this._authApiUrl}/userData`,
        dataSource: null,
        logSource: this._logSource,
        isFatal: this._isFatal,
      }),
    );
  }

  /**
   * @return {Promise<({
   *   billingType: string,
   *   category: string,
   *   customerId: string,
   *   id: string,
   *   owner: { fullName: string },
   *   paidBy: { id: number },
   *   resource: { installedResource: { "@type": string, subscriptionNumber: string } }[],
   *   status: string
   *   switchOnDate: string
   * }[])>}
   * @private
   */
  _getSubscriptionInfoSearch() {
    return cacheWithErrorCheck(this._cache, `subscriptionInfo.search`, async () => {
      const { customerId } = await this._getLoggedInSubscriptionInfo();
      return request({
        method: 'GET',
        route: `${this._subscriptionApiUrl}`,
        query: { customerId, doReturnExtendedResponse: true },
        dataSource: SUBSCRIPTION,
        logSource: this._logSource,
        isFatal: this._isFatal,
      });
    });
  }

  /**
   * @param {string} subscriptionId
   * @return {Promise<{
   *   billingType: string,
   *   category: string,
   *   customerId: string,
   *   deviceInUse: { name: string }[]
   *   firstActivationDate: string,
   *   id: string,
   *   owner: { fullName: string, doNotPublish: boolean, languagePrefference: string },
   *   paidBy: { id: number, paymentVariableSymbol: string },
   *   resource: { ownedInstalledResource: { "@type": string, id: { "@type": string } }}
   *   status: string,
   *   switchOndate: string,
   * }>}
   * @param {boolean} isFatal - subscription info can only be fatal for logged-in user
   * @see _getLoggedInSubscriptionInfo
   * @private
   */
  _getSubscriptionInfo(subscriptionId, isFatal = false) {
    return cacheWithErrorCheck(this._cache, `subscriptionInfo.${subscriptionId}`, () =>
      request({
        method: 'GET',
        route: `${this._subscriptionApiUrl}/${subscriptionId}`,
        dataSource: SUBSCRIPTION,
        logSource: this._logSource,
        isFatal,
      }),
    );
  }

  /**
   * @return {Promise<{
   *   billingType: string,
   *   category: string,
   *   customerId: string,
   *   deviceInUse: { name: string }[]
   *   firstActivationDate: string,
   *   id: string,
   *   owner: { fullName: string, doNotPublish: boolean, languagePrefference: string },
   *   paidBy: { id: number, paymentVariableSymbol: string },
   *   resource: { ownedInstalledResource: { "@type": string, id: { "@type": string } }}
   *   status: string,
   *   switchOndate: string,
   * }>}
   * @private
   */
  async _getLoggedInSubscriptionInfo() {
    const userData = await this._getUserData();
    return this._getSubscriptionInfo(userData.subId, this._isFatal);
  }
}
